import { z } from '@hono/zod-openapi'
import { zPassword } from '../common/common.model'
import { zSelectUser } from '../user/user.zod'
import { zRestAuthSelectUser } from './restauth.model'

export const zLogin = z.object({
    identifier: z.email().trim().toLowerCase(),
    password: zPassword,
})

export const zRegister = z.object({
    identifierType: z.enum(['email']).default('email'),
    identifierValue: z.email().trim().toLowerCase(),
    password: zPassword,
    firstName: z.string().min(1, { message: 'First name is required' }),
    lastName: z.string().min(1, { message: 'Last name is required' }),
    phone: z.string().optional(),
})

export const zChangePassword = z.object({
    currentPassword: zPassword,
    password: zPassword,
})

export const zForgotPassword = z.object({
    identifierType: z.enum(['email']).default('email'),
    identifierValue: z.string(),
})

export const zResetPassword = z.object({
    password: zPassword,
    passwordConfirmation: zPassword,
})

export const zForgotPasswordResponse = z.object({
    token: z.string(),
})

export const zVerifyEmail = z.object({
    token: z.string(),
})

export const zTokenRefresh = z.object({
    refreshToken: z.string(),
})

export const zLoginResponse = z.object({
    user: zSelectUser,

    restauthUser: zRestAuthSelectUser,
    userIdentifiers: z.array(
        z.object({
            identifierType: z.string(),
            identifierValue: z.string(),
            verifiedAt: z.coerce.date().nullable().optional(),
        }),
    ),
    vendor: z
        .object({
            id: z.string(),
            restauthOrganizationId: z.string(),
            name: z.string(),
        })
        .optional()
        .nullable(),
    client: z
        .object({
            id: z.string(),
            restauthOrganizationId: z.string(),
            name: z.string(),
        })
        .optional()
        .nullable(),

    organization: z
        .object({
            id: z.string(),
            name: z.string(),
        })
        .optional()
        .nullable(),
    organizationType: z.string().optional().nullable(),
    role: z.object({ id: z.string(), name: z.string() }).optional().nullable(),
    group: z
        .object({
            id: z.string(),
            name: z.string(),
        })
        .nullable()
        .optional(),
    permissions: z.object({
        enabled: z.array(z.string()),
        disabled: z.array(z.string()),
    }),
    zones: z
        .array(
            z.object({
                id: z.string(),
                name: z.string(),
            }),
        )
        .optional(),
    branches: z
        .array(
            z.object({
                id: z.string(),
                name: z.string(),
            }),
        )
        .optional(),
    accessToken: z.string(),
    refreshToken: z.string(),
    lastLogin: z.coerce.date().nullable().optional(),
    isSuperAdmin: z.boolean(),
})

export type LoginResponse = z.infer<typeof zLoginResponse>
export type LoginInput = z.infer<typeof zLogin>
export type SignupInput = z.infer<typeof zRegister>
export type ChangePasswordInput = z.infer<typeof zChangePassword>
export type ForgotPasswordInput = z.infer<typeof zForgotPassword>
export type ResetPasswordInput = z.infer<typeof zResetPassword>
export type ForgotPasswordResponse = z.infer<typeof zForgotPasswordResponse>
export type VerifyEmailInput = z.infer<typeof zVerifyEmail>
export type TokenRefreshInput = z.infer<typeof zTokenRefresh>

export type AccessTokenPayload = {
    sub: string // RestAuth userId
    exp: number
    data: {
        membershipId: string
        projectId: string
        roleId: string
        groupId: string
        organizationId: string
    }
}

export interface AuthState {
    isLoggedIn: boolean
    tokenPayload: AccessTokenPayload | null
    userDetails: LoginResponse | null
    isSuperAdmin: boolean
}
